import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../apiClient';
import './PropertyManager.css';
import MortgageDetails from './MortgageDetails';
import PortfolioValueChart from './PortfolioValueChart';

// Note: `monthlyPropertyTax` holds a YEARLY amount despite the name — the input is
// labelled "NOK/year" and the cards render it with a /year suffix. Divide by 12 here
// or the portfolio totals silently disagree with the cards they sit above.
const summarise = (properties) => {
  const rows = Array.isArray(properties) ? properties : [];

  return rows.reduce((acc, p) => {
    const value = parseFloat(p.latestValue) || 0;
    const debt = parseFloat(p.currentLoanBalance) || 0;
    const interest = parseFloat(p.monthlyPaymentInterest) || 0;
    const otherCosts =
      (parseFloat(p.monthlyCommonCosts) || 0) +
      (parseFloat(p.monthlyExtraCosts) || 0) +
      (parseFloat(p.monthlyLoanFee) || 0) +
      (parseFloat(p.monthlyPropertyTax) || 0) / 12;

    // Only count rent actually being collected. A lease that has ended still has
    // rentalInfo attached, and counting it would overstate real cash flow.
    const rent = parseFloat(p.rentalInfo?.monthlyRentalIncome) || 0;
    const isRented = !!p.rentalInfo?.isCurrentlyRented;

    acc.count += 1;
    acc.totalValue += value;
    acc.totalDebt += debt;
    acc.interest += interest;
    acc.otherCosts += otherCosts;
    acc.income += isRented ? rent : 0;
    acc.dormantIncome += isRented ? 0 : rent;
    return acc;
  }, {
    count: 0, totalValue: 0, totalDebt: 0,
    interest: 0, otherCosts: 0, income: 0, dormantIncome: 0
  });
};

const PortfolioSummary = ({ properties }) => {
  const { t } = useTranslation();
  const s = useMemo(() => summarise(properties), [properties]);

  // The page-wide formatter carries two decimals, which is pure noise on a
  // multi-million hero figure. Property values are never meaningful to the øre.
  const kr = useMemo(() => {
    const fmt = new Intl.NumberFormat('no-NO', {
      style: 'currency', currency: 'NOK', maximumFractionDigits: 0
    });
    return (n) => fmt.format(n);
  }, []);

  if (s.count === 0) return null;

  const equity = s.totalValue - s.totalDebt;
  const ltv = s.totalValue > 0 ? (s.totalDebt / s.totalValue) * 100 : 0;
  const net = s.income - s.interest - s.otherCosts;

  // Norwegian mortgage pricing steps at these loan-to-value thresholds, so the
  // nearest one below is the genuinely actionable part of the number.
  const nextBand = [60, 75, 85].find((band) => ltv > band);
  const toBand = nextBand ? s.totalDebt - (s.totalValue * nextBand) / 100 : 0;

  const ltvTone = ltv > 85 ? 'is-warn' : ltv > 75 ? 'is-caution' : 'is-good';

  return (
    <section className="portfolio-summary" aria-label={t('property.summaryTitle')}>
      <div className="ps-headline">
        <div className="ps-hero">
          <span className="ps-label">{t('property.summaryTotalValue')}</span>
          <strong className="ps-hero-value">{kr(s.totalValue)}</strong>
          <span className="ps-hero-sub">
            {t('property.summaryEquity')} {kr(equity)}
            {' · '}
            {t('property.summaryDebt')} {kr(s.totalDebt)}
          </span>
        </div>

        <div className={`ps-ltv ${ltvTone}`}>
          <span className="ps-label">{t('property.summaryLtv')}</span>
          <strong className="ps-ltv-value">{ltv.toFixed(1)}%</strong>
          <div className="ps-ltv-bar" role="presentation">
            <span style={{ width: `${Math.min(100, ltv)}%` }} />
          </div>
          <span className="ps-ltv-note">
            {nextBand
              ? t('property.summaryLtvToBand', {
                  amount: kr(toBand),
                  band: nextBand
                })
              : t('property.summaryLtvClear')}
          </span>
        </div>
      </div>

      <div className="ps-metrics">
        <div className="ps-metric">
          <span className="ps-label">{t('property.summaryInterest')}</span>
          <strong className="ps-metric-value is-cost">−{kr(s.interest)}</strong>
          <span className="ps-metric-note">{t('property.summaryPerMonth')}</span>
        </div>

        <div className="ps-metric">
          <span className="ps-label">{t('property.summaryOtherCosts')}</span>
          <strong className="ps-metric-value is-cost">−{kr(s.otherCosts)}</strong>
          <span className="ps-metric-note">{t('property.summaryOtherCostsNote')}</span>
        </div>

        <div className="ps-metric">
          <span className="ps-label">{t('property.summaryIncome')}</span>
          <strong className="ps-metric-value is-income">+{kr(s.income)}</strong>
          <span className="ps-metric-note">
            {s.dormantIncome > 0
              ? t('property.summaryIncomeDormant', { amount: kr(s.dormantIncome) })
              : t('property.summaryPerMonth')}
          </span>
        </div>

        <div className="ps-metric ps-metric-net">
          <span className="ps-label">{t('property.summaryNet')}</span>
          <strong className={`ps-metric-value ${net >= 0 ? 'is-income' : 'is-cost'}`}>
            {net >= 0 ? '+' : '−'}{kr(Math.abs(net))}
          </strong>
          <span className="ps-metric-note">{t('property.summaryNetNote')}</span>
        </div>
      </div>
    </section>
  );
};

const PropertyManager = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [valueSnapshots, setValueSnapshots] = useState([]);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const editFormRef = useRef(null);
  const [newProperty, setNewProperty] = useState({
    name: '',
    address: '',
    purchaseDate: '',
    purchasePrice: '',
    closingCosts: '',
    propertyType: 'Apartment',
    // Mortgage fields
    currentLoanBalance: '',
    currentInterestRate: '',
    loanType: '',
    repaymentTermYears: '',
    repaymentTermMonths: '',
    monthlyPaymentInterest: '',
    monthlyPaymentPrincipal: '',
    monthlyLoanFee: '',
    // Ongoing costs
    monthlyCommonCosts: '',
    monthlyPropertyTax: '',
    monthlyExtraCosts: '',
    plannedExtraMonthlyPayment: '',
    // Rental flag
    isRental: false,
    // Rental info (only sent if isRental = true)
    rentalInfo: {
      monthlyRentalIncome: '',
      monthlyRentalTax: '',
      securityDeposit: '',
      tenantName: '',
      tenantEmail: '',
      tenantPhone: '',
      leaseStartDate: '',
      leaseEndDate: '',
      isCurrentlyRented: false,
      notes: ''
    },
    notes: ''
  });

  // Fetch properties from API
  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const raw = await apiFetchJson(instance, '/api/PropertiesDev');
      const data = raw?.$values && Array.isArray(raw.$values) ? raw.$values : Array.isArray(raw) ? raw : [];
      setProperties(data);
    } catch (err) {
      console.error("Error loading properties:", err);
      setError(`Error loading properties: ${err.message}`);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper: Calculate closing costs (2.5% of purchase price)
  const calculateClosingCosts = () => {
    if (newProperty.purchasePrice) {
      const suggested = parseFloat(newProperty.purchasePrice) * 0.025;
      setNewProperty({...newProperty, closingCosts: suggested.toFixed(0)});
    }
  };

  // Helper: Calculate security deposit (3x monthly rent)
  const calculateSecurityDeposit = () => {
    if (newProperty.rentalInfo.monthlyRentalIncome) {
      const suggested = parseFloat(newProperty.rentalInfo.monthlyRentalIncome) * 3;
      setNewProperty({
        ...newProperty,
        rentalInfo: {...newProperty.rentalInfo, securityDeposit: suggested.toFixed(0)}
      });
    }
  };

  // Helper: Calculate rental tax (22% of taxable income after deductions)
  const calculateRentalTax = () => {
    const income = parseFloat(newProperty.rentalInfo.monthlyRentalIncome) || 0;
    const commonCosts = parseFloat(newProperty.monthlyCommonCosts) || 0;
    const extraCosts = parseFloat(newProperty.monthlyExtraCosts) || 0;
    const yearlyPropertyTax = parseFloat(newProperty.monthlyPropertyTax) || 0;
    const taxableIncome = Math.max(0, income - commonCosts - extraCosts - (yearlyPropertyTax / 12));
    const tax = taxableIncome * 0.22;
    setNewProperty({
      ...newProperty,
      rentalInfo: {...newProperty.rentalInfo, monthlyRentalTax: Math.max(0, tax).toFixed(0)}
    });
  };

  // Helper: Calculate monthly interest and principal from loan details
  const calculateMonthlyPayments = () => {
    const balance = parseFloat(newProperty.currentLoanBalance) || 0;
    const annualRate = parseFloat(newProperty.currentInterestRate) || 0;
    const years = parseInt(newProperty.repaymentTermYears) || 0;
    const months = parseInt(newProperty.repaymentTermMonths) || 0;
    const totalMonths = years * 12 + months;
    const loanType = parseInt(newProperty.loanType);

    if (balance <= 0 || annualRate <= 0) return;

    const monthlyRate = annualRate / 12 / 100;
    const monthlyInterest = balance * monthlyRate;

    if (loanType === 1) {
      // Rammelån: interest only, no principal
      setNewProperty({
        ...newProperty,
        monthlyPaymentInterest: Math.round(monthlyInterest).toString(),
        monthlyPaymentPrincipal: '0'
      });
    } else if (totalMonths > 0) {
      // Nedbetalingslån: annuity formula
      const annuityPayment = balance * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1);
      const principal = annuityPayment - monthlyInterest;
      setNewProperty({
        ...newProperty,
        monthlyPaymentInterest: Math.round(monthlyInterest).toString(),
        monthlyPaymentPrincipal: Math.round(principal).toString()
      });
    }
  };

  // Start editing a property
  const startEdit = async (property) => {
    setEditingProperty(property);
    setNewProperty({
      name: property.name || '',
      address: property.address || '',
      purchaseDate: property.purchaseDate ? property.purchaseDate.split('T')[0] : '',
      purchasePrice: property.purchasePrice || '',
      closingCosts: property.closingCosts || '',
      propertyType: property.propertyType || 'Apartment',
      currentLoanBalance: property.currentLoanBalance || '',
      currentInterestRate: property.currentInterestRate || '',
      loanType: property.loanType !== null && property.loanType !== undefined ? property.loanType.toString() : '',
      repaymentTermYears: property.repaymentTermYears || '',
      repaymentTermMonths: property.repaymentTermMonths || '',
      monthlyPaymentInterest: property.monthlyPaymentInterest || '',
      monthlyPaymentPrincipal: property.monthlyPaymentPrincipal || '',
      monthlyLoanFee: property.monthlyLoanFee || '',
      monthlyCommonCosts: property.monthlyCommonCosts || '',
      monthlyPropertyTax: property.monthlyPropertyTax || '',
      monthlyExtraCosts: property.monthlyExtraCosts || '',
      plannedExtraMonthlyPayment: property.plannedExtraMonthlyPayment || '',
      isRental: property.isRental || false,
      rentalInfo: property.rentalInfo ? {
        monthlyRentalIncome: property.rentalInfo.monthlyRentalIncome || '',
        monthlyRentalTax: property.rentalInfo.monthlyRentalTax || '',
        securityDeposit: property.rentalInfo.securityDeposit || '',
        tenantName: property.rentalInfo.tenantName || '',
        tenantEmail: property.rentalInfo.tenantEmail || '',
        tenantPhone: property.rentalInfo.tenantPhone || '',
        leaseStartDate: property.rentalInfo.leaseStartDate ? property.rentalInfo.leaseStartDate.split('T')[0] : '',
        leaseEndDate: property.rentalInfo.leaseEndDate ? property.rentalInfo.leaseEndDate.split('T')[0] : '',
        isCurrentlyRented: property.rentalInfo.isCurrentlyRented || false,
        notes: property.rentalInfo.notes || ''
      } : {
        monthlyRentalIncome: '',
        monthlyRentalTax: '',
        securityDeposit: '',
        tenantName: '',
        tenantEmail: '',
        tenantPhone: '',
        leaseStartDate: '',
        leaseEndDate: '',
        isCurrentlyRented: false,
        notes: ''
      },
      notes: property.notes || ''
    });
    setShowAddForm(true);
    // Scroll to edit form after render
    setTimeout(() => {
      editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    // Fetch value snapshots for editing
    try {
      const raw = await apiFetchJson(instance, `/api/PropertiesDev/${property.id}/value-snapshots`);
      const data = raw?.$values && Array.isArray(raw.$values) ? raw.$values : Array.isArray(raw) ? raw : [];
      // Filter out the purchase-price baseline entry (has no id)
      setValueSnapshots(data.filter(s => s.id).map(s => ({
        ...s,
        snapshotDate: s.snapshotDate ? s.snapshotDate.split('T')[0] : '',
        isNew: false,
        isModified: false
      })));
    } catch (err) {
      console.error('Error fetching value snapshots:', err);
    }
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingProperty(null);
    setShowAddForm(false);
    setNewProperty({
      name: '',
      address: '',
      purchaseDate: '',
      purchasePrice: '',
      closingCosts: '',
      propertyType: 'Apartment',
      currentLoanBalance: '',
      currentInterestRate: '',
      loanType: '',
      repaymentTermYears: '',
      repaymentTermMonths: '',
      monthlyPaymentInterest: '',
      monthlyPaymentPrincipal: '',
      monthlyLoanFee: '',
      monthlyCommonCosts: '',
      monthlyPropertyTax: '',
      monthlyExtraCosts: '',
      plannedExtraMonthlyPayment: '',
      isRental: false,
      rentalInfo: {
        monthlyRentalIncome: '',
        monthlyRentalTax: '',
        securityDeposit: '',
        tenantName: '',
        tenantEmail: '',
        tenantPhone: '',
        leaseStartDate: '',
        leaseEndDate: '',
        isCurrentlyRented: false,
        notes: ''
      },
      notes: ''
    });
    setValueSnapshots([]);
  };

  // Snapshot editing — wired to property detail view
  /* eslint-disable no-unused-vars */
  const addSnapshotRow = () => {
    setValueSnapshots([...valueSnapshots, {
      id: `new-${Date.now()}`,
      estimatedValue: '',
      snapshotDate: new Date().toISOString().split('T')[0],
      source: 'Manual',
      isNew: true,
      isModified: false
    }]);
  };

  const updateSnapshotRow = (index, field, value) => {
    setValueSnapshots(prev => prev.map((s, i) => 
      i === index ? { ...s, [field]: value, isModified: !s.isNew } : s
    ));
  };

  const saveSnapshot = async (snapshot, index) => {
    if (!editingProperty) return;
    const body = {
      estimatedValue: parseFloat(snapshot.estimatedValue) || 0,
      snapshotDate: snapshot.snapshotDate + 'T12:00:00.000Z',
      source: snapshot.source || 'Manual'
    };
    try {
      if (snapshot.isNew) {
        await apiFetchJson(instance, `/api/PropertiesDev/${editingProperty.id}/value-snapshots`, {
          method: 'POST', body: JSON.stringify(body)
        });
      } else {
        await apiFetchJson(instance, `/api/PropertiesDev/${editingProperty.id}/value-snapshots/${snapshot.id}`, {
          method: 'PUT', body: JSON.stringify(body)
        });
      }
      // Re-fetch to get proper IDs for new rows
      const raw = await apiFetchJson(instance, `/api/PropertiesDev/${editingProperty.id}/value-snapshots`);
      const data = raw?.$values && Array.isArray(raw.$values) ? raw.$values : Array.isArray(raw) ? raw : [];
      setValueSnapshots(data.filter(s => s.id).map(s => ({
        ...s, snapshotDate: s.snapshotDate ? s.snapshotDate.split('T')[0] : '',
        isNew: false, isModified: false
      })));
      fetchProperties();
    } catch (err) {
      console.error('Error saving snapshot:', err);
    }
  };
  /* eslint-enable no-unused-vars */

  // eslint-disable-next-line no-unused-vars
  const deleteSnapshot = async (snapshot, index) => {
    if (snapshot.isNew) {
      setValueSnapshots(prev => prev.filter((_, i) => i !== index));
      return;
    }
    if (!editingProperty) return;
    try {
      await apiFetchJson(instance, `/api/PropertiesDev/${editingProperty.id}/value-snapshots/${snapshot.id}`, {
        method: 'DELETE'
      });
      setValueSnapshots(prev => prev.filter((_, i) => i !== index));
      fetchProperties();
    } catch (err) {
      console.error('Error deleting snapshot:', err);
    }
  };

  // Add new property
  const addProperty = async (e) => {
    e.preventDefault();
    try {
      // Build property object
      const propertyData = {
        name: newProperty.name,
        address: newProperty.address,
        propertyType: newProperty.propertyType,
        purchaseDate: newProperty.purchaseDate + 'T12:00:00.000Z',
        purchasePrice: parseFloat(newProperty.purchasePrice) || 0,
        closingCosts: parseFloat(newProperty.closingCosts) || 0,
        currentLoanBalance: parseFloat(newProperty.currentLoanBalance) || 0,
        currentInterestRate: parseFloat(newProperty.currentInterestRate) || 0,
        loanType: newProperty.loanType ? parseInt(newProperty.loanType) : null,
        repaymentTermYears: newProperty.repaymentTermYears ? parseInt(newProperty.repaymentTermYears) : null,
        repaymentTermMonths: newProperty.repaymentTermMonths ? parseInt(newProperty.repaymentTermMonths) : null,
        monthlyPaymentInterest: parseFloat(newProperty.monthlyPaymentInterest) || 0,
        monthlyPaymentPrincipal: parseFloat(newProperty.monthlyPaymentPrincipal) || 0,
        monthlyLoanFee: parseFloat(newProperty.monthlyLoanFee) || 0,
        monthlyCommonCosts: parseFloat(newProperty.monthlyCommonCosts) || 0,
        monthlyPropertyTax: parseFloat(newProperty.monthlyPropertyTax) || 0,
        monthlyExtraCosts: parseFloat(newProperty.monthlyExtraCosts) || 0,
        plannedExtraMonthlyPayment: parseFloat(newProperty.plannedExtraMonthlyPayment) || 0,
        isRental: newProperty.isRental,
        notes: newProperty.notes
      };

      // Add rental info if property is a rental
      if (newProperty.isRental) {
        propertyData.rentalInfo = {
          monthlyRentalIncome: parseFloat(newProperty.rentalInfo.monthlyRentalIncome) || 0,
          monthlyRentalTax: parseFloat(newProperty.rentalInfo.monthlyRentalTax) || 0,
          securityDeposit: parseFloat(newProperty.rentalInfo.securityDeposit) || 0,
          tenantName: newProperty.rentalInfo.tenantName || null,
          tenantEmail: newProperty.rentalInfo.tenantEmail || null,
          tenantPhone: newProperty.rentalInfo.tenantPhone || null,
          leaseStartDate: newProperty.rentalInfo.leaseStartDate ? newProperty.rentalInfo.leaseStartDate + 'T12:00:00.000Z' : null,
          leaseEndDate: newProperty.rentalInfo.leaseEndDate ? newProperty.rentalInfo.leaseEndDate + 'T12:00:00.000Z' : null,
          isCurrentlyRented: newProperty.rentalInfo.isCurrentlyRented,
          notes: newProperty.rentalInfo.notes || null
        };
      }

      // Decide if creating or updating
      const endpoint = editingProperty 
        ? `/api/PropertiesDev/${editingProperty.id}`
        : `/api/PropertiesDev`;
      const method = editingProperty ? 'PUT' : 'POST';

      await apiFetchJson(instance, endpoint, {
        method: method,
        body: JSON.stringify(propertyData)
      });

      // Reset form and editing state
      cancelEdit();
      fetchProperties(); // Refresh the list
    } catch (err) {
      console.error('Error adding property:', err);
      setError('Error adding property: ' + err.message);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const addValueSnapshot = async (propertyId, estimatedValue, source) => {
    try {
      await apiFetchJson(instance, `/api/PropertiesDev/${propertyId}/value-snapshots`, {
        method: 'POST',
        body: JSON.stringify({
          estimatedValue: parseFloat(estimatedValue),
          snapshotDate: new Date().toISOString(),
          source: source,
          notes: ''
        })
      });

      fetchProperties();
    } catch (err) {
      setError('Error adding value snapshot: ' + err.message);
    }
  };

  // Delete property
  const deleteProperty = async (propertyId) => {
    if (!window.confirm(t('property.deleteConfirm'))) {
      return;
    }
    
    try {
      await apiFetchJson(instance, `/api/PropertiesDev/${propertyId}`, {
        method: 'DELETE',
      });

      fetchProperties(); // Refresh the list
    } catch (err) {
      setError('Error deleting property: ' + err.message);
    }
  };

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('no-NO');
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="property-manager">
      <div className="header">
        <div className="header-content">
          <div className="header-title">
            <h1>{t('property.title')}</h1>
            <p className="header-subtitle">{t('property.subtitle')}</p>
          </div>
          <button 
            className="add-button"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <span className="add-icon">{showAddForm ? '✕' : '+'}</span>
            {showAddForm ? t('common.cancel') : t('property.addProperty')}
          </button>
        </div>
      </div>

      <PortfolioSummary properties={properties} />

      {showAddForm && (
        <div className="add-form-container" ref={editFormRef}>          <h2>{editingProperty ? t('property.editProperty') : t('property.addNewProperty')}</h2>
          <form onSubmit={addProperty} className="add-form">
            
            {/* Section 1: Basic Information */}
            <div className="form-section">
              <h3>{t('property.basicInfo')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('property.propertyName')} *</label>
                  <input
                    type="text"
                    value={newProperty.name}
                    onChange={(e) => setNewProperty({...newProperty, name: e.target.value})}
                    required
                    placeholder={t('property.propertyNamePlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('property.propertyType')}</label>
                  <select
                    value={newProperty.propertyType}
                    onChange={(e) => setNewProperty({...newProperty, propertyType: e.target.value})}
                  >
                    <option value="Apartment">{t('property.typeApartment')}</option>
                    <option value="House">{t('property.typeHouse')}</option>
                    <option value="Townhouse">{t('property.typeTownhouse')}</option>
                    <option value="Commercial">{t('property.typeCommercial')}</option>
                    <option value="Land">{t('property.typeLand')}</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label>{t('property.address')}</label>
                <input
                  type="text"
                  value={newProperty.address}
                  onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                  placeholder={t('property.addressPlaceholder')}
                />
              </div>
            </div>

            {/* Section 2: Purchase Details */}
            <div className="form-section">
              <h3>{t('property.purchaseDetails')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('property.purchaseDate')} *</label>
                  <input
                    type="date"
                    value={newProperty.purchaseDate}
                    onChange={(e) => setNewProperty({...newProperty, purchaseDate: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('property.purchasePrice')} *</label>
                  <input
                    type="number"
                    step="1"
                    value={newProperty.purchasePrice}
                    onChange={(e) => setNewProperty({...newProperty, purchasePrice: e.target.value})}
                    required
                    placeholder="e.g. 3500000"
                  />
                </div>
                <div className="form-group">
                  <label>{t('property.closingCosts')}</label>
                  <div className="input-with-button">
                    <input
                      type="number"
                      step="1"
                      value={newProperty.closingCosts}
                      onChange={(e) => setNewProperty({...newProperty, closingCosts: e.target.value})}
                      placeholder="e.g. 50000"
                    />
                    <button type="button" className="calc-button" onClick={calculateClosingCosts} title={t('property.closingCostsTip')}>
                      📊 {t('property.closingCostsSuggest')}
                    </button>
                  </div>
                  <small className="hint">{t('property.closingCostsTip')}</small>
                </div>
              </div>

            </div>

            {/* Section 3: Mortgage/Loan Details */}
            <div className="form-section">
              <h3>{t('property.mortgageDetails')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('property.loanBalance')}</label>
                  <input
                    type="number"
                    step="1"
                    value={newProperty.currentLoanBalance}
                    onChange={(e) => setNewProperty({...newProperty, currentLoanBalance: e.target.value})}
                    placeholder="e.g. 2800000"
                  />
                </div>
                <div className="form-group">
                  <label>{t('property.interestRate')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newProperty.currentInterestRate}
                    onChange={(e) => setNewProperty({...newProperty, currentInterestRate: e.target.value})}
                    placeholder="e.g. 4.5"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>{t('property.loanType')}</label>
                  <select
                    value={newProperty.loanType}
                    onChange={(e) => setNewProperty({...newProperty, loanType: e.target.value})}
                  >
                    <option value="">{t('property.selectLoanType')}</option>
                    <option value="0">{t('property.loanTypeVariable')}</option>
                    <option value="1">{t('property.loanTypeHeloc')}</option>
                  </select>
                  <small className="hint">{t('property.loanTypeHint')}</small>
                </div>
                <div className="form-group">
                  <label>{t('property.repaymentTerm')}</label>
                  <input
                    type="number"
                    value={newProperty.repaymentTermYears}
                    onChange={(e) => setNewProperty({...newProperty, repaymentTermYears: e.target.value})}
                    placeholder="e.g. 25"
                    min="0"
                    max="50"
                  />
                </div>
                <div className="form-group">
                  <label>{t('property.plusMonths')}</label>
                  <input
                    type="number"
                    value={newProperty.repaymentTermMonths}
                    onChange={(e) => setNewProperty({...newProperty, repaymentTermMonths: e.target.value})}
                    placeholder="e.g. 6"
                    min="0"
                    max="11"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('property.monthlyInterest')}</label>
                  <input
                    type="number"
                    step="1"
                    value={newProperty.monthlyPaymentInterest}
                    onChange={(e) => setNewProperty({...newProperty, monthlyPaymentInterest: e.target.value})}
                    placeholder="e.g. 10500"
                  />
                </div>
                <div className="form-group">
                  <label>{t('property.monthlyPrincipal')}</label>
                  <input
                    type="number"
                    step="1"
                    value={newProperty.monthlyPaymentPrincipal}
                    onChange={(e) => setNewProperty({...newProperty, monthlyPaymentPrincipal: e.target.value})}
                    placeholder="e.g. 2500"
                  />
                </div>
                <div className="form-group">
                  <label>{t('property.monthlyLoanFee')}</label>
                  <input
                    type="number"
                    step="1"
                    value={newProperty.monthlyLoanFee}
                    onChange={(e) => setNewProperty({...newProperty, monthlyLoanFee: e.target.value})}
                    placeholder="e.g. 50"
                  />
                </div>
              </div>
              <button type="button" className="calc-button calc-payments-btn" onClick={calculateMonthlyPayments} title={t('property.calculatePayments')}>
                📊 {t('property.calculatePayments')}
              </button>
            </div>

            {/* Section 4: Ongoing Costs */}
            <div className="form-section">
              <h3>{t('property.ongoingCosts')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('property.commonCosts')}</label>
                  <input
                    type="number"
                    step="1"
                    value={newProperty.monthlyCommonCosts}
                    onChange={(e) => setNewProperty({...newProperty, monthlyCommonCosts: e.target.value})}
                    placeholder="e.g. 3500"
                  />
                </div>
                <div className="form-group">
                  <label>{t('property.propertyTax')}</label>
                  <input
                    type="number"
                    step="1"
                    value={newProperty.monthlyPropertyTax}
                    onChange={(e) => setNewProperty({...newProperty, monthlyPropertyTax: e.target.value})}
                    placeholder="e.g. 6000"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    {t('property.extraCosts')}
                    <span className="info-tooltip" title="Husleie.no subscription, insurance and electricity">ℹ️</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={newProperty.monthlyExtraCosts}
                    onChange={(e) => setNewProperty({...newProperty, monthlyExtraCosts: e.target.value})}
                    placeholder="e.g. 1500"
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Rental Information */}
            <div className="form-section rental-section">
              <div className="rental-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={newProperty.isRental}
                    onChange={(e) => setNewProperty({...newProperty, isRental: e.target.checked})}
                  />
                  <span>{t('property.isRental')}</span>
                </label>
              </div>

              {newProperty.isRental && (
                <div className="rental-fields">
                  <h4>{t('property.rentalDetails')}</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('property.monthlyRentalIncome')}</label>
                      <input
                        type="number"
                        step="1"
                        value={newProperty.rentalInfo.monthlyRentalIncome}
                        onChange={(e) => setNewProperty({
                          ...newProperty,
                          rentalInfo: {...newProperty.rentalInfo, monthlyRentalIncome: e.target.value}
                        })}
                        placeholder="e.g. 15000"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('property.monthlyRentalTax')}</label>
                      <div className="input-with-button">
                        <input
                          type="number"
                          step="1"
                          value={newProperty.rentalInfo.monthlyRentalTax}
                          onChange={(e) => setNewProperty({
                            ...newProperty,
                            rentalInfo: {...newProperty.rentalInfo, monthlyRentalTax: e.target.value}
                          })}
                          placeholder="e.g. 3000"
                        />
                        <button type="button" className="calc-button" onClick={calculateRentalTax} title={t('property.rentalTaxHint')}>
                          📊 Calculate
                        </button>
                      </div>
                      <small className="hint">{t('property.rentalTaxHint')}</small>
                    </div>
                    <div className="form-group">
                      <label>{t('property.securityDeposit')}</label>
                      <div className="input-with-button">
                        <input
                          type="number"
                          step="1"
                          value={newProperty.rentalInfo.securityDeposit}
                          onChange={(e) => setNewProperty({
                            ...newProperty,
                            rentalInfo: {...newProperty.rentalInfo, securityDeposit: e.target.value}
                          })}
                          placeholder="e.g. 30000"
                        />
                        <button type="button" className="calc-button" onClick={calculateSecurityDeposit} title={t('property.securityDepositTip')}>
                          📊 Calculate
                        </button>
                      </div>
                      <small className="hint">{t('property.securityDepositTip')}</small>
                    </div>
                    </div>

                    <h4>{t('property.tenantInfo')}</h4>
                  <div className="rental-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={newProperty.rentalInfo.isCurrentlyRented}
                        onChange={(e) => setNewProperty({
                          ...newProperty,
                          rentalInfo: {...newProperty.rentalInfo, isCurrentlyRented: e.target.checked}
                        })}
                      />
                      <span>{t('property.currentlyRented')}</span>
                    </label>
                  </div>

                  {newProperty.rentalInfo.isCurrentlyRented && (
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('property.tenantName')}</label>
                          <input
                            type="text"
                            value={newProperty.rentalInfo.tenantName}
                            onChange={(e) => setNewProperty({
                              ...newProperty,
                              rentalInfo: {...newProperty.rentalInfo, tenantName: e.target.value}
                            })}
                            placeholder="Full name"
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('property.tenantEmail')}</label>
                          <input
                            type="email"
                            value={newProperty.rentalInfo.tenantEmail}
                            onChange={(e) => setNewProperty({
                              ...newProperty,
                              rentalInfo: {...newProperty.rentalInfo, tenantEmail: e.target.value}
                            })}
                            placeholder="email@example.com"
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('property.tenantPhone')}</label>
                          <input
                            type="tel"
                            value={newProperty.rentalInfo.tenantPhone}
                            onChange={(e) => setNewProperty({
                              ...newProperty,
                              rentalInfo: {...newProperty.rentalInfo, tenantPhone: e.target.value}
                            })}
                            placeholder="+47 xxx xx xxx"
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('property.leaseStart')}</label>
                          <input
                            type="date"
                            value={newProperty.rentalInfo.leaseStartDate}
                            onChange={(e) => {
                              const start = e.target.value;
                              const updates = { leaseStartDate: start };
                              if (start && !newProperty.rentalInfo.leaseEndDate) {
                                const endDate = new Date(start);
                                endDate.setFullYear(endDate.getFullYear() + 3);
                                updates.leaseEndDate = endDate.toISOString().split('T')[0];
                              }
                              setNewProperty({
                                ...newProperty,
                                rentalInfo: {...newProperty.rentalInfo, ...updates}
                              });
                            }}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('property.leaseEnd')}</label>
                          <input
                            type="date"
                            value={newProperty.rentalInfo.leaseEndDate}
                            onChange={(e) => setNewProperty({
                              ...newProperty,
                              rentalInfo: {...newProperty.rentalInfo, leaseEndDate: e.target.value}
                            })}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label>{t('property.rentalNotes')}</label>
                    <textarea
                      value={newProperty.rentalInfo.notes}
                      onChange={(e) => setNewProperty({
                        ...newProperty,
                        rentalInfo: {...newProperty.rentalInfo, notes: e.target.value}
                      })}
                      rows="2"
                      placeholder={t('property.notesPlaceholder')}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Section 6: General Notes */}
            <div className="form-section">
              <div className="form-group">
                <label>{t('property.generalNotes')}</label>
                <textarea
                  value={newProperty.notes}
                  onChange={(e) => setNewProperty({...newProperty, notes: e.target.value})}
                  rows="3"
                  placeholder={t('property.notesPlaceholder')}
                />
              </div>
            </div>
            
            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={cancelEdit}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="submit-button">
                {editingProperty ? t('property.updateProperty') : t('property.addProperty')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="properties-grid">
        {Array.isArray(properties) && properties.length > 0 ? (
          properties.map((property) => (
            <PropertyCard 
              key={property.id} 
              property={property} 
              onDelete={deleteProperty}
              onEdit={startEdit}
              onRefresh={fetchProperties}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              isSelected={selectedPropertyIds.includes(property.id)}
              onToggleSelect={(id) => {
                setSelectedPropertyIds(prev => 
                  prev.includes(id) 
                    ? prev.filter(pid => pid !== id)
                    : [...prev, id]
                );
              }}
            />
          ))
        ) : (
          <div className="no-properties">
            <p>{t('property.noProperties')}</p>
          </div>
        )}
      </div>

      <PortfolioValueChart 
        properties={properties}
        selectedPropertyIds={selectedPropertyIds}
      />
    </div>
  );
};

const PropertyCard = ({ property, onDelete, onEdit, onRefresh, formatCurrency, formatDate, isSelected, onToggleSelect }) => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [showValueHistory, setShowValueHistory] = useState(false);
  const [cardSnapshots, setCardSnapshots] = useState([]);
  const [mortgageSummary, setMortgageSummary] = useState(null);
  const [mortgageSummaryLoading, setMortgageSummaryLoading] = useState(false);
  const [mortgageSubTab, setMortgageSubTab] = useState('overview');

  // What-if state
  const [whatIfExtraPayment, setWhatIfExtraPayment] = useState('');
  const [whatIfRate, setWhatIfRate] = useState('');
  const [rammelånExtraMonthly, setRammelånExtraMonthly] = useState('3000');
  const [rammelånLumpSum, setRammelånLumpSum] = useState('200000');
  const [togglingRental, setTogglingRental] = useState(false);

  // Extra deposits state
  const [extraDeposits, setExtraDeposits] = useState([]);
  const [showAddDeposit, setShowAddDeposit] = useState(false);
  const [newDeposit, setNewDeposit] = useState({ amount: '', plannedDate: '', notes: '' });
  const [newWithdrawal, setNewWithdrawal] = useState({ amount: '', notes: '' });
  const [plannedExtraMonthly, setPlannedExtraMonthly] = useState(property.plannedExtraMonthlyPayment || 0);
  const [savingExtraMonthly, setSavingExtraMonthly] = useState(false);

  // Auto-fetch mortgage data since both panels are always visible
  useEffect(() => {
    const fetchData = async () => {
      if (property.currentLoanBalance > 0 || property.loanType != null) {
        try {
          setMortgageSummaryLoading(true);
          const data = await apiFetchJson(instance, `/api/properties/${property.id}/MortgageTimeline/summary`);
          setMortgageSummary(data);
        } catch (err) {
          console.error('Error fetching mortgage summary:', err);
        } finally {
          setMortgageSummaryLoading(false);
        }
        try {
          const data = await apiFetchJson(instance, `/api/PropertiesDev/${property.id}/extra-deposits`);
          const depositsArray = Array.isArray(data) ? data : (data?.$values || []);
          setExtraDeposits(depositsArray);
        } catch (err) {
          console.error('Error fetching extra deposits:', err);
        }
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.id]);

  // Auto-save toggle for isRental
  const toggleRental = async () => {
    setTogglingRental(true);
    try {
      // Send full property data with isRental toggled (PUT requires all fields)
      const propertyData = {
        name: property.name,
        address: property.address,
        propertyType: property.propertyType,
        purchaseDate: property.purchaseDate,
        purchasePrice: property.purchasePrice,
        closingCosts: property.closingCosts || 0,
        currentLoanBalance: property.currentLoanBalance || 0,
        currentInterestRate: property.currentInterestRate || 0,
        loanType: property.loanType,
        repaymentTermYears: property.repaymentTermYears,
        repaymentTermMonths: property.repaymentTermMonths,
        monthlyPaymentInterest: property.monthlyPaymentInterest || 0,
        monthlyPaymentPrincipal: property.monthlyPaymentPrincipal || 0,
        monthlyLoanFee: property.monthlyLoanFee || 0,
        monthlyCommonCosts: property.monthlyCommonCosts || 0,
        monthlyPropertyTax: property.monthlyPropertyTax || 0,
        monthlyExtraCosts: property.monthlyExtraCosts || 0,
        plannedExtraMonthlyPayment: property.plannedExtraMonthlyPayment || 0,
        isRental: !property.isRental,
        notes: property.notes
      };
      // Preserve existing rental info if toggling on and it exists
      if (!property.isRental && property.rentalInfo) {
        propertyData.rentalInfo = property.rentalInfo;
      }
      await apiFetchJson(instance, `/api/PropertiesDev/${property.id}`, {
        method: 'PUT',
        body: JSON.stringify(propertyData)
      });
      onRefresh();
    } catch (err) {
      console.error('Error toggling rental:', err);
    } finally {
      setTogglingRental(false);
    }
  };

  // Extra deposit functions
  const fetchExtraDeposits = async () => {
    try {
      const raw = await apiFetchJson(instance, `/api/PropertiesDev/${property.id}/extra-deposits`);
      const data = Array.isArray(raw) ? raw : (raw?.$values || []);
      setExtraDeposits(data);
    } catch (err) {
      console.error('Error fetching extra deposits:', err);
    }
  };

  const saveExtraMonthly = async (value) => {
    setSavingExtraMonthly(true);
    try {
      const propertyData = {
        name: property.name,
        address: property.address,
        propertyType: property.propertyType,
        purchaseDate: property.purchaseDate,
        purchasePrice: property.purchasePrice,
        closingCosts: property.closingCosts || 0,
        currentLoanBalance: property.currentLoanBalance || 0,
        currentInterestRate: property.currentInterestRate || 0,
        loanType: property.loanType,
        repaymentTermYears: property.repaymentTermYears,
        repaymentTermMonths: property.repaymentTermMonths,
        monthlyPaymentInterest: property.monthlyPaymentInterest || 0,
        monthlyPaymentPrincipal: property.monthlyPaymentPrincipal || 0,
        monthlyLoanFee: property.monthlyLoanFee || 0,
        monthlyCommonCosts: property.monthlyCommonCosts || 0,
        monthlyPropertyTax: property.monthlyPropertyTax || 0,
        monthlyExtraCosts: property.monthlyExtraCosts || 0,
        plannedExtraMonthlyPayment: parseFloat(value) || 0,
        isRental: property.isRental,
        notes: property.notes,
        rentalInfo: property.rentalInfo || undefined
      };
      await apiFetchJson(instance, `/api/PropertiesDev/${property.id}`, {
        method: 'PUT',
        body: JSON.stringify(propertyData)
      });
      onRefresh();
      fetchMortgageSummary(true);
    } catch (err) {
      console.error('Error saving extra monthly:', err);
    } finally {
      setSavingExtraMonthly(false);
    }
  };

  const addExtraDeposit = async () => {
    if (!newDeposit.amount || !newDeposit.plannedDate) return;
    try {
      await apiFetchJson(instance, `/api/PropertiesDev/${property.id}/extra-deposits`, {
        method: 'POST',
        body: JSON.stringify({
          amount: parseFloat(newDeposit.amount),
          plannedDate: newDeposit.plannedDate + 'T12:00:00.000Z',
          notes: newDeposit.notes || null
        })
      });
      setNewDeposit({ amount: '', plannedDate: '', notes: '' });
      setShowAddDeposit(false);
      fetchExtraDeposits();
      onRefresh();
      fetchMortgageSummary(true);
    } catch (err) {
      console.error('Error adding extra deposit:', err);
    }
  };

  const applyExtraDeposit = async (depositId) => {
    try {
      await apiFetchJson(instance, `/api/PropertiesDev/${property.id}/extra-deposits/${depositId}/apply`, {
        method: 'POST'
      });
      fetchExtraDeposits();
      onRefresh();
      fetchMortgageSummary(true);
    } catch (err) {
      console.error('Error applying extra deposit:', err);
    }
  };

  const deleteExtraDeposit = async (depositId) => {
    try {
      await apiFetchJson(instance, `/api/PropertiesDev/${property.id}/extra-deposits/${depositId}`, {
        method: 'DELETE'
      });
      fetchExtraDeposits();
      onRefresh();
      fetchMortgageSummary(true);
    } catch (err) {
      console.error('Error deleting extra deposit:', err);
    }
  };

  const withdrawFromRammelån = async () => {
    if (!newWithdrawal.amount) return;
    try {
      await apiFetchJson(instance, `/api/PropertiesDev/${property.id}/extra-deposits/withdraw`, {
        method: 'POST',
        body: JSON.stringify({
          amount: parseFloat(newWithdrawal.amount),
          notes: newWithdrawal.notes || null
        })
      });
      setNewWithdrawal({ amount: '', notes: '' });
      onRefresh();
      fetchMortgageSummary(true);
    } catch (err) {
      console.error('Error withdrawing from rammelån:', err);
    }
  };

  if (!property || typeof property !== 'object') {
    return <div className="property-card error">Invalid property data</div>;
  }

  const fetchCardSnapshots = async () => {
    try {
      const raw = await apiFetchJson(instance, `/api/PropertiesDev/${property.id}/value-snapshots`);
      const data = raw?.$values && Array.isArray(raw.$values) ? raw.$values : Array.isArray(raw) ? raw : [];
      setCardSnapshots(data.map(s => ({
        ...s,
        snapshotDate: s.snapshotDate ? s.snapshotDate.split('T')[0] : '',
        isNew: false,
        isModified: false,
        isPurchase: !s.id
      })));
    } catch (err) {
      console.error('Error fetching snapshots:', err);
    }
  };

  const toggleValueHistory = () => {
    if (!showValueHistory) fetchCardSnapshots();
    setShowValueHistory(!showValueHistory);
  };

  const updateCardSnapshot = (index, field, value) => {
    setCardSnapshots(prev => prev.map((s, i) =>
      i === index ? { ...s, [field]: value, isModified: !s.isNew } : s
    ));
  };

  const addCardSnapshotRow = () => {
    setCardSnapshots([...cardSnapshots, {
      id: `new-${Date.now()}`,
      estimatedValue: '',
      snapshotDate: new Date().toISOString().split('T')[0],
      source: 'Manual',
      isNew: true, isModified: false
    }]);
  };

  const saveCardSnapshot = async (snapshot, index) => {
    const body = {
      estimatedValue: parseFloat(snapshot.estimatedValue) || 0,
      snapshotDate: snapshot.snapshotDate + 'T12:00:00.000Z',
      source: snapshot.source || 'Manual'
    };
    try {
      const url = snapshot.isNew
        ? `/api/PropertiesDev/${property.id}/value-snapshots`
        : `/api/PropertiesDev/${property.id}/value-snapshots/${snapshot.id}`;
      await apiFetchJson(instance, url, {
        method: snapshot.isNew ? 'POST' : 'PUT',
        body: JSON.stringify(body)
      });
      await fetchCardSnapshots();
      onRefresh();
    } catch (err) {
      console.error('Error saving snapshot:', err);
    }
  };

  const deleteCardSnapshot = async (snapshot, index) => {
    if (snapshot.isNew) {
      setCardSnapshots(prev => prev.filter((_, i) => i !== index));
      return;
    }
    try {
      await apiFetchJson(instance, `/api/PropertiesDev/${property.id}/value-snapshots/${snapshot.id}`, {
        method: 'DELETE'
      });
      setCardSnapshots(prev => prev.filter((_, i) => i !== index));
      onRefresh();
    } catch (err) {
      console.error('Error deleting snapshot:', err);
    }
  };

  // Save updated purchase price/date via property PUT
  const savePurchasePrice = async (snapshot) => {
    try {
      const propertyData = {
        name: property.name,
        address: property.address,
        propertyType: property.propertyType,
        purchaseDate: snapshot.snapshotDate + 'T12:00:00.000Z',
        purchasePrice: parseFloat(snapshot.estimatedValue) || 0,
        closingCosts: property.closingCosts || 0,
        currentLoanBalance: property.currentLoanBalance || 0,
        currentInterestRate: property.currentInterestRate || 0,
        loanType: property.loanType,
        repaymentTermYears: property.repaymentTermYears,
        repaymentTermMonths: property.repaymentTermMonths,
        monthlyPaymentInterest: property.monthlyPaymentInterest || 0,
        monthlyPaymentPrincipal: property.monthlyPaymentPrincipal || 0,
        monthlyLoanFee: property.monthlyLoanFee || 0,
        monthlyCommonCosts: property.monthlyCommonCosts || 0,
        monthlyPropertyTax: property.monthlyPropertyTax || 0,
        monthlyExtraCosts: property.monthlyExtraCosts || 0,
        plannedExtraMonthlyPayment: property.plannedExtraMonthlyPayment || 0,
        isRental: property.isRental,
        notes: property.notes
      };
      await apiFetchJson(instance, `/api/PropertiesDev/${property.id}`, {
        method: 'PUT', body: JSON.stringify(propertyData)
      });
      await fetchCardSnapshots();
      onRefresh();
    } catch (err) {
      console.error('Error saving purchase price:', err);
    }
  };

  const fetchMortgageSummary = async (force = false) => {
    if ((mortgageSummary && !force) || mortgageSummaryLoading) return;
    try {
      setMortgageSummaryLoading(true);
      const data = await apiFetchJson(instance, `/api/properties/${property.id}/MortgageTimeline/summary`);
      setMortgageSummary(data);
    } catch (err) {
      console.error('Error fetching mortgage summary:', err);
    } finally {
      setMortgageSummaryLoading(false);
    }
  };

  // Use default values when properties are missing
  const propertyName = property.name || "Unnamed Property";
  const propertyType = property.propertyType || "Unknown";
  const propertyAddress = property.address || "No address";
  const propertyPurchaseDate = property.purchaseDate || new Date().toISOString();
  const propertyPurchasePrice = property.purchasePrice || 0;
  const propertyNotes = property.notes || "";

  const latestValue = property.latestValue || propertyPurchasePrice;
  const valueChange = latestValue - propertyPurchasePrice;
  const valueChangePercent = propertyPurchasePrice > 0 
    ? ((valueChange / propertyPurchasePrice) * 100).toFixed(1) 
    : "0.0";

  // What-if calculations (client-side)
  const calculateWhatIf = () => {
    if (!mortgageSummary || mortgageSummary.currentBalance <= 0) return null;

    const balance = mortgageSummary.currentBalance;
    const rate = whatIfRate ? parseFloat(whatIfRate) : mortgageSummary.currentInterestRate;
    const extra = whatIfExtraPayment ? parseFloat(whatIfExtraPayment) : 0;
    const basePayment = mortgageSummary.monthlyPayment - mortgageSummary.monthlyLoanFee;
    const newPayment = basePayment + extra;
    const monthlyRate = rate / 12 / 100;

    if (newPayment <= 0 || monthlyRate < 0) return null;

    const monthlyInterest = balance * monthlyRate;
    if (newPayment <= monthlyInterest && monthlyRate > 0) return { cannotPayOff: true };

    let newRemainingMonths;
    if (monthlyRate === 0) {
      newRemainingMonths = Math.ceil(balance / newPayment);
    } else {
      const inner = 1 - (balance * monthlyRate / newPayment);
      if (inner <= 0) return { cannotPayOff: true };
      newRemainingMonths = Math.ceil(-Math.log(inner) / Math.log(1 + monthlyRate));
    }

    // Calculate total interest with new params
    let totalInterest = 0;
    let b = balance;
    for (let i = 0; i < newRemainingMonths && b > 0; i++) {
      const interest = b * monthlyRate;
      totalInterest += interest;
      b -= (newPayment - interest);
      if (b < 0) b = 0;
    }

    const originalInterest = mortgageSummary.totalRemainingInterest;
    const interestSaved = originalInterest - totalInterest;

    return {
      newRemainingMonths,
      newPayoffDate: new Date(Date.now() + newRemainingMonths * 30.44 * 24 * 60 * 60 * 1000),
      totalInterest: Math.round(totalInterest),
      interestSaved: Math.round(interestSaved),
      monthsSaved: (mortgageSummary.remainingMonths || 0) - newRemainingMonths,
      newMonthlyPayment: Math.round(newPayment + mortgageSummary.monthlyLoanFee)
    };
  };

  const whatIfResult = calculateWhatIf();

  // Rammelån what-if: how much you save with extra payments
  const calculateRammelånWhatIf = () => {
    if (!mortgageSummary || mortgageSummary.currentBalance <= 0) return null;
    const balance = mortgageSummary.currentBalance;
    const rate = mortgageSummary.currentInterestRate / 100;
    const monthlyRate = rate / 12;
    const currentMonthlyInterest = balance * monthlyRate;

    const extraMonthly = parseFloat(rammelånExtraMonthly) || 0;
    const lumpSum = parseFloat(rammelånLumpSum) || 0;

    const results = {};

    // Scenario 1: Extra monthly payment
    if (extraMonthly > 0) {
      // After 1 year: balance reduced by 12 × extraMonthly
      const years = [1, 3, 5, 10];
      results.monthly = years.map(y => {
        const months = y * 12;
        const newBalance = Math.max(0, balance - (extraMonthly * months));
        const newMonthlyInterest = newBalance * monthlyRate;
        const monthlySaved = currentMonthlyInterest - newMonthlyInterest;
        const totalExtraPaid = extraMonthly * months;
        const totalInterestSaved = (extraMonthly * monthlyRate * months * (months + 1)) / 2;
        return { years: y, newBalance, newMonthlyInterest, monthlySaved: Math.round(monthlySaved), totalExtraPaid, totalInterestSaved: Math.round(totalInterestSaved) };
      });
    }

    // Scenario 2: One-time lump sum
    if (lumpSum > 0) {
      const newBalance = Math.max(0, balance - lumpSum);
      const newMonthlyInterest = newBalance * monthlyRate;
      const monthlySaved = currentMonthlyInterest - newMonthlyInterest;
      const yearlySaved = monthlySaved * 12;
      results.lumpSum = { newBalance, newMonthlyInterest, monthlySaved: Math.round(monthlySaved), yearlySaved: Math.round(yearlySaved) };
    }

    results.currentMonthlyInterest = Math.round(currentMonthlyInterest);
    return results;
  };

  const rammelånWhatIf = mortgageSummary?.loanType === 'Rammelån' ? calculateRammelånWhatIf() : null;

  const formatRepaymentTerm = () => {
    const years = property.repaymentTermYears;
    const months = property.repaymentTermMonths || 0;
    if (!years && !months) return 'N/A';
    return t('property.yearsMonths', { years: years || 0, months });
  };

  const formatMonths = (totalMonths) => {
    if (!totalMonths) return 'N/A';
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    return t('property.yearsMonths', { years, months }) || ('0 ' + t('common.months'));
  };

  // Determine available tabs
  const hasMortgage = property.currentLoanBalance > 0 || property.loanType != null;

  return (
    <div className="property-card">
      {/* Card Header with Actions */}
      <div className="card-header-bar">
        <div className="property-header">
          <h3>{propertyName}</h3>
          <span className="property-type">{propertyType}</span>
        </div>
        <div className="card-header-actions">
          <label className={`icon-btn graph-toggle ${isSelected ? 'active' : ''}`} title="Show in graph">
            <input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(property.id)} hidden />
            📊
          </label>
          <button className="icon-btn" onClick={() => onEdit(property)} title="Edit">✏️</button>
          <button className="icon-btn" onClick={toggleValueHistory} title={showValueHistory ? 'Hide Values' : 'Update Value'}>📈</button>
          <button className="icon-btn delete-btn" onClick={() => onDelete(property.id)} title="Delete">🗑️</button>
        </div>
      </div>

      {/* Two-Panel Layout */}
      <div className="property-panels">
        {/* === LEFT PANEL: Property Info === */}
        <div className="panel panel-info">
          <div className="panel-header-row">
            <h4 className="panel-title">{'📋 ' + t('property.panelInfo')}</h4>
            <label className="rental-chip" title="Mark as rental property">
              <input
                type="checkbox"
                checked={property.isRental}
                onChange={toggleRental}
                disabled={togglingRental}
              />
              <span>{togglingRental ? t('common.saving') : t('property.rental')}</span>
            </label>
          </div>

          <div className="property-details">
            <p className="address">{propertyAddress}</p>

            <div className="current-value">
              <div>
                <label>{t('property.currentValue')}</label>
                <span className="value">{formatCurrency(latestValue)}</span>
              </div>
              <div className={`value-change ${valueChange >= 0 ? 'positive' : 'negative'}`}>
                <span>{valueChange >= 0 ? '+' : ''}{formatCurrency(valueChange)}</span>
                <span>({valueChange >= 0 ? '+' : ''}{valueChangePercent}%)</span>
              </div>
            </div>

            <div className="purchase-details-subtle">
              <span>{t('property.purchased', { date: formatDate(propertyPurchaseDate) })}</span>
              <span className="subtle-separator">·</span>
              <span>{formatCurrency(propertyPurchasePrice)}</span>
              {property.closingCosts > 0 && (
                <>
                  <span className="subtle-separator">·</span>
                  <span>{t('property.closing', { amount: formatCurrency(property.closingCosts) })}</span>
                </>
              )}
            </div>

            {(property.monthlyCommonCosts > 0 || property.monthlyPropertyTax > 0 || property.monthlyExtraCosts > 0) && (
              <div className="ongoing-costs-group">
                {property.monthlyCommonCosts > 0 && (
                  <div className="ongoing-costs">
                    <label>{t('property.commonCostsLabel')}:</label>
                    <span>{formatCurrency(property.monthlyCommonCosts)}{t('common.perMonth')}</span>
                  </div>
                )}
                {property.monthlyPropertyTax > 0 && (
                  <div className="ongoing-costs">
                    <label>{t('property.propertyTaxLabel')}:</label>
                    <span>{formatCurrency(property.monthlyPropertyTax)}{t('common.perYear')}</span>
                  </div>
                )}
                {property.monthlyExtraCosts > 0 && (
                  <div className="ongoing-costs">
                    <label>{t('property.extraCostsLabel')}:</label>
                    <span>{formatCurrency(property.monthlyExtraCosts)}{t('common.perMonth')}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {propertyNotes && (
            <div className="property-notes">
              <p>{propertyNotes}</p>
            </div>
          )}

          {/* Value History */}
          {showValueHistory && (
            <div className="card-value-history">
              <h4 className="subsection-title">{t('property.valueHistory')}</h4>
              <div className="value-history-list">
                {cardSnapshots.length > 0 ? (
                  cardSnapshots.map((snapshot, index) => (
                    <div key={snapshot.id || `purchase-${index}`} className={`value-history-row ${snapshot.isPurchase ? 'purchase-row' : ''} ${snapshot.isNew ? 'new-row' : ''} ${snapshot.isModified ? 'modified-row' : ''}`}>
                      <div className="form-group">
                        <label>{snapshot.isPurchase ? t('property.purchaseDate') : 'Date'}</label>
                        <input type="date" value={snapshot.snapshotDate}
                          onChange={(e) => updateCardSnapshot(index, 'snapshotDate', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>{snapshot.isPurchase ? t('property.purchasePrice') : t('property.valueNok')}</label>
                        <input type="number" step="1" value={snapshot.estimatedValue}
                          onChange={(e) => updateCardSnapshot(index, 'estimatedValue', e.target.value)} placeholder="e.g. 4000000" />
                      </div>
                      <div className="form-group">
                        <label>{t('property.source')}</label>
                        {snapshot.isPurchase ? (
                          <input type="text" value={t('property.purchase')} disabled />
                        ) : (
                          <select value={snapshot.source} onChange={(e) => updateCardSnapshot(index, 'source', e.target.value)}>
                            <option value="Manual">{t('property.sourceManual')}</option>
                            <option value="Realtor">{t('property.sourceRealtor')}</option>
                            <option value="Appraisal">{t('property.sourceAppraisal')}</option>
                            <option value="Market">{t('property.sourceMarket')}</option>
                            <option value="Online">{t('property.sourceOnline')}</option>
                          </select>
                        )}
                      </div>
                      <div className="value-history-actions">
                        {(snapshot.isNew || snapshot.isModified) && (
                          <button type="button" className="save-row-btn" onClick={() => snapshot.isPurchase ? savePurchasePrice(snapshot) : saveCardSnapshot(snapshot, index)} title="Save">💾</button>
                        )}
                        {!snapshot.isPurchase && (
                          <button type="button" className="delete-row-btn" onClick={() => deleteCardSnapshot(snapshot, index)} title="Delete">🗑️</button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="empty-hint">{t('property.noValueUpdates')}</p>
                )}
              </div>
              <button type="button" className="add-row-btn" onClick={addCardSnapshotRow}>{t('property.addValueUpdate')}</button>
            </div>
          )}
        </div>

        {/* === RENTAL PANEL === */}
        {property.isRental && (
          <div className="panel panel-rental">
            <h4 className="panel-title">{'🏠 ' + t('property.panelRental')}</h4>

            {property.rentalInfo && (() => {
              const rentalIncome = parseFloat(property.rentalInfo.monthlyRentalIncome) || 0;
              const felleskost = parseFloat(property.monthlyCommonCosts) || 0;
              const extraCosts = parseFloat(property.monthlyExtraCosts) || 0;
              const yearlyPropertyTax = parseFloat(property.monthlyPropertyTax) || 0;
              const calculatedTax = Math.max(0, (rentalIncome - felleskost - extraCosts - (yearlyPropertyTax / 12)) * 0.22);
              const netIncome = rentalIncome - calculatedTax;
              return (
                <>
                  <div className="mortgage-section">
                    <h4>{t('property.incomeTax')}</h4>
                    <div className="profitability-breakdown">
                      <div className="breakdown-row">
                        <span className="breakdown-label">{t('property.monthlyIncome')}</span>
                        <span className="breakdown-value positive">{formatCurrency(rentalIncome)}</span>
                      </div>
                      <div className="breakdown-row">
                        <span className="breakdown-label">{t('property.monthlyTax')}</span>
                        <span className="breakdown-value">- {formatCurrency(calculatedTax)}</span>
                      </div>
                      <div className="breakdown-divider total-divider" />
                      <div className="breakdown-row total-row">
                        <span className="breakdown-label"><strong>{t('property.netIncome')}</strong></span>
                        <span className={`breakdown-value ${netIncome >= 0 ? 'positive' : 'negative'}`}><strong>{formatCurrency(netIncome)}</strong></span>
                      </div>
                    </div>
                  </div>

                  <div className="mortgage-section">
                    <h4>{t('property.leaseStatus')}</h4>
                    <div className="profitability-breakdown">
                      <div className="breakdown-row">
                        <span className="breakdown-label">{t('property.currentlyRentedLabel')}</span>
                        <span className="breakdown-value">{property.rentalInfo.isCurrentlyRented ? '✅ Yes' : '❌ No'}</span>
                      </div>
                      {property.rentalInfo.leaseStartDate && (
                        <div className="breakdown-row">
                           <span className="breakdown-label">{t('property.leaseStartLabel')}</span>
                          <span className="breakdown-value">{formatDate(property.rentalInfo.leaseStartDate)}</span>
                        </div>
                      )}
                      {property.rentalInfo.leaseEndDate && (
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.leaseEndLabel')}</span>
                          <span className="breakdown-value">{formatDate(property.rentalInfo.leaseEndDate)}</span>
                        </div>
                      )}
                      <div className="breakdown-row">
                        <span className="breakdown-label">{t('property.securityDepositLabel')}</span>
                        <span className="breakdown-value">{formatCurrency(property.rentalInfo.securityDeposit)}</span>
                      </div>
                      {property.rentalInfo.isCurrentlyRented && (property.rentalInfo.tenantName || property.rentalInfo.tenantEmail || property.rentalInfo.tenantPhone) && (
                        <>
                          <div className="breakdown-divider"></div>
                          {property.rentalInfo.tenantName && (
                            <div className="breakdown-row">
                              <span className="breakdown-label">{t('property.name')}</span>
                              <span className="breakdown-value">{property.rentalInfo.tenantName}</span>
                            </div>
                          )}
                          {property.rentalInfo.tenantEmail && (
                            <div className="breakdown-row">
                              <span className="breakdown-label">{t('property.email')}</span>
                              <span className="breakdown-value">{property.rentalInfo.tenantEmail}</span>
                            </div>
                          )}
                          {property.rentalInfo.tenantPhone && (
                            <div className="breakdown-row">
                              <span className="breakdown-label">{t('property.phone')}</span>
                              <span className="breakdown-value">{property.rentalInfo.tenantPhone}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Profitability */}
            {(() => {
              const monthlyIncome = parseFloat(property.rentalInfo?.monthlyRentalIncome) || 0;
              const felleskost = parseFloat(property.monthlyCommonCosts) || 0;
              const extraCosts = parseFloat(property.monthlyExtraCosts) || 0;
              const yearlyPropertyTax = parseFloat(property.monthlyPropertyTax) || 0;
              const monthlyPropertyTax = yearlyPropertyTax / 12;
              const monthlyInterest = parseFloat(property.monthlyPaymentInterest) || 0;
              const interestAfterDeduction = monthlyInterest * 0.78;
              const totalExpenses = felleskost + extraCosts + monthlyPropertyTax + interestAfterDeduction;
              const taxableIncome = Math.max(0, monthlyIncome - felleskost - extraCosts - monthlyPropertyTax);
              const rentalTax = taxableIncome * 0.22;
              const netMonthly = monthlyIncome - totalExpenses - rentalTax;
              if (monthlyIncome === 0) return null;
              return (
                <div className="mortgage-section">
                  <h4>💰 {t('property.profitability')}</h4>
                  <div className="profitability-breakdown">
                    <div className="breakdown-row income-row">
                      <span className="breakdown-label">{t('property.rentalIncome')}</span>
                      <span className="breakdown-value positive">+ {formatCurrency(monthlyIncome)}</span>
                    </div>
                    <div className="breakdown-divider" />
                    <div className="breakdown-row">
                      <span className="breakdown-label">{t('property.totalExpenses')}</span>
                      <span className="breakdown-value">- {formatCurrency(totalExpenses + rentalTax)}</span>
                    </div>
                    <div className="breakdown-divider total-divider" />
                    <div className={`breakdown-row total-row ${netMonthly >= 0 ? 'positive' : 'negative'}`}>
                      <span className="breakdown-label"><strong>{t('property.netMonthly')}</strong></span>
                      <span className="breakdown-value"><strong>{formatCurrency(netMonthly)}</strong></span>
                    </div>
                    <div className="breakdown-row yearly-row">
                      <span className="breakdown-label">{t('property.netYearly')}</span>
                      <span className={`breakdown-value ${netMonthly >= 0 ? 'positive' : 'negative'}`}>
                        <strong>{formatCurrency(netMonthly * 12)}</strong>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* === RIGHT PANEL: Mortgage Manager === */}
        {hasMortgage && (
          <div className="panel panel-mortgage">
            <h4 className="panel-title">{'🏦 ' + t('property.panelMortgage')}</h4>
            {mortgageSummaryLoading ? (
              <div className="loading">{t('property.loadingMortgage')}</div>
            ) : mortgageSummary ? (
              <>
                {/* Sub-tab navigation */}
                <div className="mortgage-sub-tabs">
                  <button className={`sub-tab ${mortgageSubTab === 'overview' ? 'active' : ''}`} onClick={() => setMortgageSubTab('overview')}>
                    {'📊 ' + t('property.loanOverview')}
                  </button>
                  <button className={`sub-tab ${mortgageSubTab === 'payments' ? 'active' : ''}`} onClick={() => setMortgageSubTab('payments')}>
                    {'💰 ' + t('property.extraPayments')}
                  </button>
                  <button className={`sub-tab ${mortgageSubTab === 'whatif' ? 'active' : ''}`} onClick={() => setMortgageSubTab('whatif')}>
                    {'🔮 ' + t('property.whatIf')}
                  </button>
                  <button className={`sub-tab ${mortgageSubTab === 'timeline' ? 'active' : ''}`} onClick={() => setMortgageSubTab('timeline')}>
                    {'📅 ' + t('property.timeline')}
                  </button>
                </div>

                {/* === OVERVIEW SUB-TAB === */}
                {mortgageSubTab === 'overview' && (
                  <div className="mortgage-sub-panel">
                    {/* Loan Overview */}
                    <div className="mortgage-section">
                      <h4>{t('property.loanOverview')}</h4>
                      <div className="profitability-breakdown">
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.balance')}</span>
                          <span className="breakdown-value">{formatCurrency(mortgageSummary.currentBalance)}</span>
                        </div>
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.rate')}</span>
                          <span className="breakdown-value">{mortgageSummary.currentInterestRate.toFixed(2)}%</span>
                        </div>
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.type')}</span>
                          <span className="breakdown-value">{mortgageSummary.loanType === 'Variable' ? t('property.repaymentLoan') : t('property.heloc')}</span>
                        </div>
                        <div className="breakdown-row">
                          <span className="breakdown-label">LTV (Belåningsgrad)</span>
                          <span className="breakdown-value">{mortgageSummary.ltv.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Monthly Breakdown */}
                    <div className="mortgage-section">
                      <h4>{t('property.monthlyPayment')}</h4>
                      <div className="profitability-breakdown">
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.interest')}</span>
                          <span className="breakdown-value">{formatCurrency(mortgageSummary.monthlyInterest)}</span>
                        </div>
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.principal')}</span>
                          <span className="breakdown-value">{formatCurrency(mortgageSummary.monthlyPrincipal)}</span>
                        </div>
                        {mortgageSummary.monthlyLoanFee > 0 && (
                          <div className="breakdown-row">
                            <span className="breakdown-label">{t('property.fee')}</span>
                            <span className="breakdown-value">{formatCurrency(mortgageSummary.monthlyLoanFee)}</span>
                          </div>
                        )}
                        {mortgageSummary.plannedExtraMonthlyPayment > 0 && (
                          <div className="breakdown-row">
                            <span className="breakdown-label">{t('property.extraDeposits')}</span>
                            <span className="breakdown-value positive">{formatCurrency(mortgageSummary.plannedExtraMonthlyPayment)}</span>
                          </div>
                        )}
                        <div className="breakdown-divider total-divider" />
                        <div className="breakdown-row total-row">
                          <span className="breakdown-label"><strong>{t('property.totalMonthly')}</strong></span>
                          <span className="breakdown-value"><strong>{formatCurrency(mortgageSummary.monthlyPayment)}</strong></span>
                        </div>
                      </div>
                    </div>

                    {/* Repayment */}
                    <div className="mortgage-section">
                      <h4>{t('property.remaining')}</h4>
                      <div className="profitability-breakdown">
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.term')}</span>
                          <span className="breakdown-value">{formatRepaymentTerm()}</span>
                        </div>
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.remaining')}</span>
                          <span className="breakdown-value">
                            {mortgageSummary.remainingMonths != null 
                              ? formatMonths(mortgageSummary.remainingMonths)
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.paidOff')}</span>
                          <span className="breakdown-value">
                            {mortgageSummary.payoffDate 
                              ? new Date(mortgageSummary.payoffDate).toLocaleDateString('nb-NO', { year: 'numeric', month: 'long' })
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.totalInterest')}</span>
                          <span className="breakdown-value">{formatCurrency(mortgageSummary.totalRemainingInterest)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Interest after tax deduction */}
                    <div className="mortgage-section">
                      <h4>{t('property.interestAfterDeduction')}</h4>
                      <div className="profitability-breakdown">
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.interest')}</span>
                          <span className="breakdown-value">{formatCurrency(mortgageSummary.annualInterest)}</span>
                        </div>
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.interestAfterDeduction')}</span>
                          <span className="breakdown-value positive">{formatCurrency(mortgageSummary.skattefradrag)}{t('common.perYear')}</span>
                        </div>
                        <div className="breakdown-row">
                          <span className="breakdown-label">{t('property.interestAfterDeduction')}</span>
                          <span className="breakdown-value positive">{formatCurrency(mortgageSummary.skattefradrag / 12)}{t('common.perMonth')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment History Summary */}
                    {(mortgageSummary.totalInterestPaidSoFar > 0 || mortgageSummary.totalPrincipalPaidSoFar > 0) && (
                      <div className="mortgage-section">
                        <h4>{t('property.paymentHistory')}</h4>
                        <div className="profitability-breakdown">
                          <div className="breakdown-row">
                            <span className="breakdown-label">{t('property.totalInterest')}</span>
                            <span className="breakdown-value">{formatCurrency(mortgageSummary.totalInterestPaidSoFar)}</span>
                          </div>
                          <div className="breakdown-row">
                            <span className="breakdown-label">{t('property.totalCost')}</span>
                            <span className="breakdown-value">{formatCurrency(mortgageSummary.totalPrincipalPaidSoFar)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* === EXTRA PAYMENTS SUB-TAB === */}
                {mortgageSubTab === 'payments' && (
                  <div className="mortgage-sub-panel">
                    {mortgageSummary.currentBalance > 0 ? (
                      <>
                        {/* Recurring Monthly Extra */}
                        <div className="mortgage-section extra-deposits-section">
                          <h4>📅 {t('property.monthlyExtra')}</h4>
                          <p className="section-hint">Pay extra each month to reduce your mortgage faster.</p>
                          <div className="extra-monthly-row">
                            <div className="extra-monthly-input">
                              <input
                                type="number"
                                step="100"
                                value={plannedExtraMonthly}
                                onChange={(e) => setPlannedExtraMonthly(e.target.value)}
                                placeholder="0"
                              />
                              <span className="input-suffix">kr{t('common.perMonth')}</span>
                              <button
                                className="btn-save-extra"
                                onClick={() => saveExtraMonthly(plannedExtraMonthly)}
                                disabled={savingExtraMonthly || parseFloat(plannedExtraMonthly) === (property.plannedExtraMonthlyPayment || 0)}
                              >
                                {savingExtraMonthly ? '...' : t('common.save')}
                              </button>
                            </div>
                            {mortgageSummary.plannedExtraMonthlyPayment > 0 && (
                              <span className="extra-monthly-active">
                                ✅ Active — {formatCurrency(mortgageSummary.plannedExtraMonthlyPayment)}{t('common.perMonth')} extra
                              </span>
                            )}
                          </div>
                        </div>

                        {/* One-Time Deposits */}
                        <div className="mortgage-section extra-deposits-section">
                          <div className="deposits-header">
                            <h4>💰 {t('property.plannedDeposits')}</h4>
                            <button className="btn-add-deposit" onClick={() => setShowAddDeposit(!showAddDeposit)}>
                              {showAddDeposit ? ('✕ ' + t('common.cancel')) : ('+ ' + t('common.add'))}
                            </button>
                          </div>
                          <p className="section-hint">Plan and track lump-sum payments toward your mortgage.</p>

                          {showAddDeposit && (
                            <div className="add-deposit-form">
                              <div className="form-row">
                                <input
                                  type="number"
                                  step="1000"
                                  value={newDeposit.amount}
                                  onChange={(e) => setNewDeposit({ ...newDeposit, amount: e.target.value })}
                                  placeholder={t('property.amount')}
                                />
                                <input
                                  type="date"
                                  value={newDeposit.plannedDate}
                                  onChange={(e) => setNewDeposit({ ...newDeposit, plannedDate: e.target.value })}
                                />
                              </div>
                              <div className="form-row">
                                <input
                                  type="text"
                                  value={newDeposit.notes}
                                  onChange={(e) => setNewDeposit({ ...newDeposit, notes: e.target.value })}
                                  placeholder={t('property.notes')}
                                />
                                <button className="btn-confirm-deposit" onClick={addExtraDeposit}>
                                  {t('common.add')}
                                </button>
                              </div>
                            </div>
                          )}

                          {extraDeposits.length > 0 ? (
                            <div className="deposits-list">
                              {extraDeposits.map(deposit => (
                                <div key={deposit.id} className={`deposit-item ${deposit.isApplied ? 'applied' : ''}`}>
                                  <div className="deposit-info">
                                    <span className="deposit-amount">{formatCurrency(deposit.amount)}</span>
                                    <span className="deposit-date">
                                      {new Date(deposit.plannedDate).toLocaleDateString('nb-NO', { year: 'numeric', month: 'short', day: 'numeric' })}
                                    </span>
                                    {deposit.notes && <span className="deposit-notes">{deposit.notes}</span>}
                                  </div>
                                  <div className="deposit-actions">
                                    {deposit.isApplied ? (
                                      <span className="deposit-applied-badge">✅ {t('property.applied')}</span>
                                    ) : (
                                      <>
                                        <button className="btn-apply-deposit" onClick={() => applyExtraDeposit(deposit.id)} title={t('property.apply')}>
                                          {t('property.apply')}
                                        </button>
                                        <button className="btn-delete-deposit" onClick={() => deleteExtraDeposit(deposit.id)} title="Remove">
                                          ✕
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {extraDeposits.filter(d => !d.isApplied).length > 0 && (
                                <div className="deposits-total">
                                  Total pending: <strong>{formatCurrency(extraDeposits.filter(d => !d.isApplied).reduce((sum, d) => sum + d.amount, 0))}</strong>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="empty-deposits">{t('property.noDeposits')}</p>
                          )}
                        </div>

                        {/* Rammelån Withdrawal */}
                        {mortgageSummary.loanType === 'Rammelån' && (
                          <div className="mortgage-section withdrawal-section">
                            <h4>🏦 {t('property.withdrawFromHeloc')}</h4>
                            <p className="section-hint">Draw funds from your frame loan. This increases your balance and monthly interest.</p>
                            <div className="add-deposit-form">
                              <div className="form-row">
                                <input
                                  type="number"
                                  step="1000"
                                  value={newWithdrawal.amount}
                                  onChange={(e) => setNewWithdrawal({ ...newWithdrawal, amount: e.target.value })}
                                  placeholder={t('property.withdrawAmount')}
                                />
                                <input
                                  type="text"
                                  value={newWithdrawal.notes}
                                  onChange={(e) => setNewWithdrawal({ ...newWithdrawal, notes: e.target.value })}
                                  placeholder={t('property.withdrawNotes')}
                                />
                                <button className="btn-withdraw" onClick={withdrawFromRammelån} disabled={!newWithdrawal.amount}>
                                  {t('property.withdraw')}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="empty-state">Mortgage is fully paid off! 🎉</p>
                    )}
                  </div>
                )}

                {/* === WHAT-IF SUB-TAB === */}
                {mortgageSubTab === 'whatif' && (
                  <div className="mortgage-sub-panel">
                    {mortgageSummary.currentBalance > 0 ? (
                      <>
                        {/* Nedbetalingslån What-If */}
                        {mortgageSummary.loanType !== 'Rammelån' && (
                          <div className="mortgage-section what-if-section">
                            <h4>🔮 {t('property.whatIf')}</h4>
                            <p className="section-hint">See how extra payments or rate changes affect your mortgage.</p>
                            <div className="what-if-inputs">
                              <div className="form-group">
                                <label>{t('property.whatIfExtraPayment')}</label>
                                <input
                                  type="number"
                                  step="100"
                                  value={whatIfExtraPayment}
                                  onChange={(e) => setWhatIfExtraPayment(e.target.value)}
                                  placeholder="e.g. 2000"
                                />
                              </div>
                              <div className="form-group">
                                <label>{t('property.whatIfRate')}</label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={whatIfRate}
                                  onChange={(e) => setWhatIfRate(e.target.value)}
                                  placeholder={`Current: ${mortgageSummary.currentInterestRate}%`}
                                />
                              </div>
                            </div>

                            {whatIfResult && !whatIfResult.cannotPayOff && (whatIfExtraPayment || whatIfRate) && (
                              <div className="what-if-results">
                                <div className="profitability-breakdown">
                                  <div className="breakdown-row">
                                    <span className="breakdown-label">{t('property.remaining')}</span>
                                    <span className="breakdown-value">{formatMonths(whatIfResult.newRemainingMonths)}</span>
                                  </div>
                                  <div className="breakdown-row">
                                    <span className="breakdown-label">{t('property.paidOff')}</span>
                                    <span className="breakdown-value">
                                      {whatIfResult.newPayoffDate.toLocaleDateString('nb-NO', { year: 'numeric', month: 'long' })}
                                    </span>
                                  </div>
                                  <div className="breakdown-row">
                                    <span className="breakdown-label">{t('property.totalInterest')}</span>
                                    <span className="breakdown-value">{formatCurrency(whatIfResult.totalInterest)}</span>
                                  </div>
                                  {whatIfResult.interestSaved > 0 && (
                                    <div className="breakdown-row">
                                      <span className="breakdown-label">{t('property.totalInterest')}</span>
                                      <span className="breakdown-value positive">{formatCurrency(whatIfResult.interestSaved)}</span>
                                    </div>
                                  )}
                                  {whatIfResult.monthsSaved > 0 && (
                                    <div className="breakdown-row">
                                      <span className="breakdown-label">Time Saved</span>
                                      <span className="breakdown-value positive">{formatMonths(whatIfResult.monthsSaved)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {whatIfResult && whatIfResult.cannotPayOff && (
                              <div className="what-if-warning">
                                ⚠️ Payment does not cover interest at this rate. Loan will never pay off.
                              </div>
                            )}
                          </div>
                        )}

                        {/* Rammelån What-If */}
                        {mortgageSummary.loanType === 'Rammelån' && rammelånWhatIf && (
                          <div className="mortgage-section what-if-section">
                            <h4>🔮 {t('property.helocPayoffScenarios')}</h4>
                            <p className="section-hint">See how much you save on interest by paying down the balance.</p>
                            <div className="what-if-inputs">
                              <div className="form-group">
                                <label>{t('property.helocExtraMonthly')}</label>
                                <input
                                  type="number"
                                  step="500"
                                  value={rammelånExtraMonthly}
                                  onChange={(e) => setRammelånExtraMonthly(e.target.value)}
                                  placeholder="e.g. 3000"
                                />
                              </div>
                              <div className="form-group">
                                <label>{t('property.helocLumpSum')}</label>
                                <input
                                  type="number"
                                  step="10000"
                                  value={rammelånLumpSum}
                                  onChange={(e) => setRammelånLumpSum(e.target.value)}
                                  placeholder="e.g. 200000"
                                />
                              </div>
                            </div>

                            <div className="rammelån-current">
                              <span>{t('property.interest')}: <strong>{formatCurrency(rammelånWhatIf.currentMonthlyInterest)}</strong></span>
                            </div>

                            {rammelånWhatIf.lumpSum && (
                              <div className="what-if-results">
                                <h5>💰 {t('property.withLumpSum', { amount: formatCurrency(parseFloat(rammelånLumpSum)) })}</h5>
                                <div className="profitability-breakdown">
                                  <div className="breakdown-row">
                                    <span className="breakdown-label">{t('property.balance')}</span>
                                    <span className="breakdown-value">{formatCurrency(rammelånWhatIf.lumpSum.newBalance)}</span>
                                  </div>
                                  <div className="breakdown-row">
                                    <span className="breakdown-label">{t('property.interest')}</span>
                                    <span className="breakdown-value">{formatCurrency(rammelånWhatIf.lumpSum.newMonthlyInterest)}</span>
                                  </div>
                                  <div className="breakdown-row">
                                    <span className="breakdown-label">{t('property.totalInterest')}</span>
                                    <span className="breakdown-value positive">{formatCurrency(rammelånWhatIf.lumpSum.monthlySaved)}{t('common.perMonth')}</span>
                                  </div>
                                  <div className="breakdown-row">
                                    <span className="breakdown-label">{t('property.totalInterest')}</span>
                                    <span className="breakdown-value positive">{formatCurrency(rammelånWhatIf.lumpSum.yearlySaved)}{t('common.perYear')}</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {rammelånWhatIf.monthly && (
                              <div className="what-if-results">
                                <h5>📅 {t('property.withMonthlyExtra', { amount: formatCurrency(parseFloat(rammelånExtraMonthly)) })}</h5>
                                <table className="rammelån-table">
                                  <thead>
                                    <tr>
                                      <th>{t('property.term')}</th>
                                      <th>{t('property.extraPayments')}</th>
                                      <th>{t('property.balance')}</th>
                                      <th>{t('property.interest')}</th>
                                      <th>{t('property.totalInterest')}</th>
                                      <th>{t('property.totalInterest')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rammelånWhatIf.monthly.map(row => (
                                      <tr key={row.years}>
                                        <td>{t('property.yearsMonths', { years: row.years, months: 0 })}</td>
                                        <td>{formatCurrency(row.totalExtraPaid)}</td>
                                        <td>{formatCurrency(row.newBalance)}</td>
                                        <td>{formatCurrency(row.newMonthlyInterest)}</td>
                                        <td className="positive">{formatCurrency(row.monthlySaved)}</td>
                                        <td className="positive">{formatCurrency(row.totalInterestSaved)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="empty-state">Mortgage is fully paid off! 🎉</p>
                    )}
                  </div>
                )}

                {/* === TIMELINE SUB-TAB === */}
                {mortgageSubTab === 'timeline' && (
                  <div className="mortgage-sub-panel">
                    <MortgageDetails
                      propertyId={property.id}
                      propertyName={propertyName}
                      onClose={() => {}}
                      embedded={true}
                    />
                  </div>
                )}
              </>
            ) : (
              <p className="empty-state">{t('property.noMortgageData')}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyManager;